import { useEffect, useMemo } from 'react'

import { useAvailableCreatives } from 'dataQueries/purchaseFlowRevampCreatives.query'
import { useKeyPickupTravelCost } from 'dataQueries/purchaseFlowRevampKeyPickupTravelCost.query'

import { useResolvedStepFlow } from '../../routing/useResolvedStepFlow'
import { usePfStore } from '../../store'
import type { CartExtraSnapshot } from '../../store/slices/cartSlice'
import {
  keyPickupTravelQuoteRequest,
  offerableExtras,
  resolveExtraServiceFees,
  resolveFavouriteCreativeTravelFee,
  resolveKeyPickupTravelFee,
  resolveOrganizationFee,
  resolveSchedulingFee
} from './extras.helpers'
import { useInstructionCatalogue } from './useInstructionCatalogue'
import { usePfSubscriptionEntitlement } from './usePfSubscriptionEntitlement'

/**
 * The fee lines the instructions answers add to the order, for the cart total and the payment
 * intent. Placement derives the same fees on the backend, so these must follow every change that
 * moves them — including a cart line removed on a later step, which re-prices the options or skips
 * the step altogether. Mounted once for the whole flow; a skipped step contributes no fee.
 */
export const computeInstructionFees = (input: {
  isSkipped: boolean
  extraServices: Parameters<typeof resolveExtraServiceFees>[0]
  extrasCatalogue: Parameters<typeof resolveExtraServiceFees>[1]
  meeting: Parameters<typeof resolveOrganizationFee>[0]
  organizationCatalogue: Parameters<typeof resolveOrganizationFee>[1]
  ctBookingId: Parameters<typeof resolveFavouriteCreativeTravelFee>[0]
  creatives: Parameters<typeof resolveFavouriteCreativeTravelFee>[1]
  travelQuote: Parameters<typeof resolveKeyPickupTravelFee>[1]
}): CartExtraSnapshot[] => {
  if (input.isSkipped) return []
  const fees = resolveExtraServiceFees(input.extraServices, input.extrasCatalogue)
  const optional = [
    resolveOrganizationFee(input.meeting, input.organizationCatalogue),
    resolveSchedulingFee(input.meeting, input.organizationCatalogue),
    resolveFavouriteCreativeTravelFee(input.ctBookingId, input.creatives),
    resolveKeyPickupTravelFee(input.meeting, input.travelQuote),
  ]
  return [...fees, ...optional.filter((fee): fee is CartExtraSnapshot => Boolean(fee))]
}

export const useInstructionFeesMirror = (): void => {
  const meeting = usePfStore((s) => s.meeting)
  const ctBookingId = usePfStore((s) => s.ctBookingId)
  const extraServices = usePfStore((s) => s.extraServices)
  const setExtrasFromInstructions = usePfStore((s) => s.setExtrasFromInstructions)
  const setInstructionFeesUnknown = usePfStore((s) => s.setInstructionFeesUnknown)

  const isSkipped = !useResolvedStepFlow().steps.includes('instructions')
  const instructionCatalogue = useInstructionCatalogue()
  const isSubscribed = usePfSubscriptionEntitlement()
  const { creatives, status: creativesStatus } = useAvailableCreatives({ enabled: ctBookingId !== undefined })

  const travelQuoteKey = JSON.stringify(keyPickupTravelQuoteRequest(meeting))
  const travelQuoteRequest = useMemo(() => JSON.parse(travelQuoteKey) as ReturnType<typeof keyPickupTravelQuoteRequest>, [travelQuoteKey])
  const travelQuote = useKeyPickupTravelCost(travelQuoteRequest)

  const extrasCatalogue = useMemo(
    () => offerableExtras(instructionCatalogue.extraServices, isSubscribed),
    [instructionCatalogue.extraServices, isSubscribed]
  )

  useEffect(() => {
    setExtrasFromInstructions(computeInstructionFees({
      isSkipped,
      extraServices,
      extrasCatalogue,
      meeting,
      organizationCatalogue: instructionCatalogue.organizationInstructions,
      ctBookingId,
      creatives,
      travelQuote: travelQuote.data,
    }))
  }, [isSkipped, extraServices, extrasCatalogue, meeting, instructionCatalogue.organizationInstructions, ctBookingId, creatives, travelQuote.data, setExtrasFromInstructions])

  const isFavouriteFeeUnknown = !!ctBookingId && creativesStatus !== 'ready'
  const isTravelFeeUnknown = !!travelQuoteRequest && !travelQuote.data
  const isUnknown = !isSkipped && (isFavouriteFeeUnknown || isTravelFeeUnknown)
  useEffect(() => {
    setInstructionFeesUnknown(isUnknown)
  }, [isUnknown, setInstructionFeesUnknown])
}
